import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Filter, X, Plus, Hash, ToggleLeft, ToggleRight } from "lucide-react";
import type { FilterConfig as IFilterConfig } from "../types";

interface FilterConfigProps {
  filters: IFilterConfig;
  onUpdateFilters: (updated: IFilterConfig) => void;
  readOnly?: boolean;
}

export default function FilterConfig({ filters, onUpdateFilters, readOnly = false }: FilterConfigProps) {
  const { t } = useTranslation("filters");
  const [posInput, setPosInput] = useState("");
  const [negInput, setNegInput] = useState("");
  const [hashInput, setHashInput] = useState("");

  const handleAddPositive = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = posInput.trim();
    if (clean && !filters.positiveKeywords.includes(clean)) {
      onUpdateFilters({
        ...filters,
        positiveKeywords: [...filters.positiveKeywords, clean]
      });
      setPosInput("");
    }
  };

  const handleAddNegative = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = negInput.trim();
    if (clean && !filters.negativeKeywords.includes(clean)) {
      onUpdateFilters({
        ...filters,
        negativeKeywords: [...filters.negativeKeywords, clean]
      });
      setNegInput("");
    }
  };

  const handleAddHashtag = (e: React.FormEvent) => {
    e.preventDefault();
    let clean = hashInput.trim();
    if (!clean) return;
    if (!clean.startsWith("#")) {
      clean = `#${clean}`;
    }
    if (clean && !filters.requiredHashtags.includes(clean)) {
      onUpdateFilters({
        ...filters,
        requiredHashtags: [...filters.requiredHashtags, clean]
      });
      setHashInput("");
    }
  };

  const removePositive = (kw: string) => {
    onUpdateFilters({
      ...filters,
      positiveKeywords: filters.positiveKeywords.filter(x => x !== kw)
    });
  };

  const removeNegative = (kw: string) => {
    onUpdateFilters({
      ...filters,
      negativeKeywords: filters.negativeKeywords.filter(x => x !== kw)
    });
  };

  const removeHashtag = (hash: string) => {
    onUpdateFilters({
      ...filters,
      requiredHashtags: filters.requiredHashtags.filter(x => x !== hash)
    });
  };

  const toggleCaseSensitive = () => {
    onUpdateFilters({
      ...filters,
      caseSensitive: !filters.caseSensitive
    });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5">
      <div className="flex justify-between items-center border-b border-slate-100 pb-4 mb-6">
        <div>
          <h2 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
            <Filter className="w-5 h-5 text-sky-500" />
            {t("header.title")}
          </h2>
          <p className="text-slate-500 text-xs mt-0.5 font-sans">
            {t("header.description")}
          </p>
        </div>

        {/* Case-sensitivity control */}
        <button
          type="button"
          onClick={toggleCaseSensitive}
          disabled={readOnly}
          aria-pressed={filters.caseSensitive}
          aria-label={filters.caseSensitive ? t("caseSensitive.enabled") : t("caseSensitive.disabled")}
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-100 px-3 py-1.5 rounded-lg transition-colors cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
        >
          {filters.caseSensitive ? (
            <ToggleRight className="w-5 h-5 text-sky-600" />
          ) : (
            <ToggleLeft className="w-5 h-5 text-slate-400" />
          )}
          <span>{t("caseSensitive.label")}</span>
        </button>
      </div>

      <div className="space-y-6">
        {/* Positive include rules */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
            {t("positive.label")}
          </label>
          {!readOnly && (
            <form onSubmit={handleAddPositive} className="flex gap-2 mb-3">
              <input
                type="text"
                dir="auto"
                aria-label={t("positive.inputLabel")}
                placeholder={t("positive.placeholder")}
                value={posInput}
                onChange={(e) => setPosInput(e.target.value)}
                className="flex-1 px-3 py-2 border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 rounded-lg text-sm bg-slate-50/50 outline-hidden font-sans placeholder-slate-400"
              />
              <button
                type="submit"
                aria-label={t("actions.addPositive")}
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold text-xs px-3.5 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </form>
          )}
          
          {filters.positiveKeywords.length === 0 ? (
            <p className="text-slate-400 text-xs italic">
              {t("positive.empty")}
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {filters.positiveKeywords.map((kw) => (
                <span
                  key={kw}
                  className="inline-flex items-center gap-1 bg-sky-50 border border-sky-100 text-sky-800 text-xs ps-2.5 pe-1.5 py-1 rounded-lg font-medium"
                  dir="auto"
                >
                  {kw}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removePositive(kw)}
                      aria-label={t("positive.remove", { value: kw })}
                      className="hover:bg-sky-100 rounded-full p-0.5 text-sky-600 transition-all cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Required hashtag rules */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
            {t("hashtags.label")}
          </label>
          {!readOnly && (
            <form onSubmit={handleAddHashtag} className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <span className="absolute start-3 top-2.5 text-slate-400 text-sm font-medium">#</span>
                <input
                  type="text"
                  dir="auto"
                  aria-label={t("hashtags.inputLabel")}
                  placeholder={t("hashtags.placeholder")}
                  value={hashInput}
                  onChange={(e) => setHashInput(e.target.value)}
                  className="w-full ps-7 pe-3 py-2 border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 rounded-lg text-sm bg-slate-50/50 outline-hidden font-sans placeholder-slate-400"
                />
              </div>
              <button
                type="submit"
                aria-label={t("actions.addHashtag")}
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold text-xs px-3.5 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </form>
          )}

          {filters.requiredHashtags.length === 0 ? (
            <p className="text-slate-400 text-xs italic">
              {t("hashtags.empty")}
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {filters.requiredHashtags.map((hash) => (
                <span
                  key={hash}
                  className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-100 text-indigo-800 text-xs ps-2.5 pe-1.5 py-1 rounded-lg font-medium"
                  dir="auto"
                >
                  <Hash className="w-3 h-3 text-indigo-500 shrink-0" />
                  {hash.replace(/^#/, "")}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removeHashtag(hash)}
                      aria-label={t("hashtags.remove", { value: hash })}
                      className="hover:bg-indigo-100 rounded-full p-0.5 text-indigo-600 transition-all cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Negative exclude rules */}
        <div className="border-t border-slate-100 pt-5">
          <label className="block text-xs font-bold uppercase tracking-wider text-rose-700 mb-2">
            {t("negative.label")}
          </label>
          {!readOnly && (
            <form onSubmit={handleAddNegative} className="flex gap-2 mb-3">
              <input
                type="text"
                dir="auto"
                aria-label={t("negative.inputLabel")}
                placeholder={t("negative.placeholder")}
                value={negInput}
                onChange={(e) => setNegInput(e.target.value)}
                className="flex-1 px-3 py-2 border border-slate-200 focus:border-rose-300 focus:ring-2 focus:ring-rose-50 rounded-lg text-sm bg-slate-50/50 outline-hidden font-sans placeholder-slate-400"
              />
              <button
                type="submit"
                aria-label={t("actions.addNegative")}
                className="bg-rose-900 hover:bg-rose-800 text-white font-semibold text-xs px-3.5 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </form>
          )}

          {filters.negativeKeywords.length === 0 ? (
            <p className="text-slate-400 text-xs italic">
              {t("negative.empty")}
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {filters.negativeKeywords.map((kw) => (
                <span
                  key={kw}
                  className="inline-flex items-center gap-1 bg-rose-50 border border-rose-100 text-rose-800 text-xs ps-2.5 pe-1.5 py-1 rounded-lg font-medium"
                  dir="auto"
                >
                  {kw}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removeNegative(kw)}
                      aria-label={t("negative.remove", { value: kw })}
                      className="hover:bg-rose-100 rounded-full p-0.5 text-rose-600 transition-all cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
